import { useCallback, useEffect, useRef, type KeyboardEvent as ReactKeyboardEvent } from 'react';
import { X } from 'lucide-react';
import {
  DecisionPreviewTabActions,
  DecisionPreviewTabBody,
} from '@/components/case/jurisprudence/DecisionPreviewTab';
import { useDismissiblePanel } from '@/hooks/useDismissiblePanel';
import { cn } from '@/lib/utils';
import {
  selectPreviewPanelIsOpen,
  usePreviewPanelStore,
  type PreviewTabKey,
} from '@/stores/previewPanelStore';
import { DocumentPreviewTabBody, DocumentPreviewTabFileActions, DocumentPreviewTabViewToggle } from './DocumentPreviewTab';
import { PreviewIconButton } from './previewPanelChrome';

function tabDomId(tabKey: PreviewTabKey): string {
  return `document-preview-tab-${encodeURIComponent(tabKey)}`;
}

function focusPreviewTab(tabKey: PreviewTabKey): void {
  document.getElementById(tabDomId(tabKey))?.focus();
}

/**
 * Emplacement latéral unique du workspace : une tablist, un corps, une largeur.
 *
 * Ce composant ne connaît QUE la coque — identité d'onglet, ordre, onglet actif,
 * capacité, cycle de vie et chrome. Ce que montre un onglet, et surtout comment
 * il le charge, appartient au module de son type : `DocumentPreviewTab` pour une
 * pièce (URL signée S3, scopée cabinet), `DecisionPreviewTab` pour un arrêt
 * (corpus global immuable, cache TanStack partagé avec `/decisions/:ordre/:id`).
 * Cette frontière est délibérée — cf. `components/case/jurisprudence/CLAUDE.md`.
 */
export function DocumentPreviewPanel() {
  const panelRef = useRef<HTMLElement>(null);
  const focusRestoreTargetRef = useRef<HTMLElement | null>(null);
  const wasOpenRef = useRef(false);
  const isOpen = usePreviewPanelStore(selectPreviewPanelIsOpen);
  const tabOrder = usePreviewPanelStore((state) => state.tabOrder);
  const tabsById = usePreviewPanelStore((state) => state.tabsById);
  const activeTabId = usePreviewPanelStore((state) => state.activeTabId);
  const activateTab = usePreviewPanelStore((state) => state.activateTab);
  const closeTab = usePreviewPanelStore((state) => state.closeTab);
  const closePanel = usePreviewPanelStore((state) => state.closePanel);
  const activeTab = activeTabId ? tabsById[activeTabId] : undefined;
  useDismissiblePanel(isOpen, closePanel);

  useEffect(() => {
    // The final tab removes its own controls from the DOM, so retain the external opener
    // explicitly instead of letting browser focus fall back to an unrelated page element.
    if (isOpen && !wasOpenRef.current) {
      const focusedElement = document.activeElement;
      if (
        focusedElement instanceof HTMLElement
        && !panelRef.current?.contains(focusedElement)
      ) {
        focusRestoreTargetRef.current = focusedElement;
      }
    } else if (!isOpen && wasOpenRef.current) {
      const restoreTarget = focusRestoreTargetRef.current;
      focusRestoreTargetRef.current = null;
      if (restoreTarget?.isConnected) restoreTarget.focus();
    }
    wasOpenRef.current = isOpen;
  }, [isOpen]);

  const handleTabKeyDown = useCallback((event: ReactKeyboardEvent<HTMLButtonElement>, tabKey: PreviewTabKey) => {
    const currentIndex = tabOrder.indexOf(tabKey);
    if (currentIndex < 0) return;

    let nextIndex: number | null = null;
    if (event.key === 'ArrowRight') nextIndex = (currentIndex + 1) % tabOrder.length;
    if (event.key === 'ArrowLeft') nextIndex = (currentIndex - 1 + tabOrder.length) % tabOrder.length;
    if (event.key === 'Home') nextIndex = 0;
    if (event.key === 'End') nextIndex = tabOrder.length - 1;
    if (nextIndex === null) return;

    event.preventDefault();
    const nextTabKey = tabOrder[nextIndex];
    if (!nextTabKey) return;
    activateTab(nextTabKey);
    focusPreviewTab(nextTabKey);
  }, [activateTab, tabOrder]);

  const handleCloseTab = useCallback((tabKey: PreviewTabKey) => {
    const closedIndex = tabOrder.indexOf(tabKey);
    const shouldTransferFocus = tabKey === activeTabId;
    const nextTabKey = shouldTransferFocus
      ? (tabOrder[closedIndex + 1] ?? tabOrder[closedIndex - 1])
      : activeTabId ?? undefined;
    closeTab(tabKey);
    if (nextTabKey) focusPreviewTab(nextTabKey);
  }, [activeTabId, closeTab, tabOrder]);

  const activeTabDomId = activeTabId ? tabDomId(activeTabId) : undefined;

  return (
    <aside
      ref={panelRef}
      aria-label="Aperçu"
      className={cn(
        'flex flex-col bg-white',
        // Deux modes, en CSS pur — aucun `useMediaQuery`, donc aucun démontage au
        // franchissement du seuil : un arbre différent par breakpoint rechargerait
        // tous les PDF et iframes ouverts à la moindre rotation de tablette.
        //
        // Sous md : overlay plein écran qui glisse par-dessus la page, comme le dock
        // assistant. z-panel reste au-dessus des surfaces non modales (bordereau,
        // renumérotation) et sous les toasts.
        'fixed right-0 top-0 z-panel h-full w-full transform border-l border-grey-200 shadow-[-8px_0_30px_-12px_rgba(0,0,0,0.15)] transition-transform duration-300 ease-in-out',
        isOpen ? 'translate-x-0' : 'pointer-events-none translate-x-full',
        // À partir de md : colonne du layout. Le contenu principal est `flex-1
        // min-w-0`, il rétrécit donc tout seul — plus de marge en dur à tenir
        // d'accord avec la largeur du panneau. Fermé, `--preview-w` vaut 0px.
        'md:static md:z-auto md:w-[var(--preview-w)] md:translate-x-0 md:overflow-hidden md:border-l-0 md:shadow-none',
        isOpen ? 'md:shrink-0' : 'md:pointer-events-none',
      )}
    >
      {/* Ni fond gris ni filet sous la chrome : la rangée d'actions et le contenu
          forment une seule surface blanche. Seule la bande d'ONGLETS reste en
          retrait — c'est ce retrait qui fait exister l'onglet actif, blanc,
          au-dessus des autres. */}
      <div className="flex flex-shrink-0 flex-col">
        <div
          role="tablist"
          aria-label="Documents ouverts"
          // `items-center` et non `items-end` : les onglets ne sont plus amarrés au
          // bas de la bande (ils n'ont plus de bordure qui s'ouvre vers le contenu),
          // ils y flottent — d'où une pastille plus courte que la bande, centrée.
          className="flex h-11 min-w-0 items-center gap-1 overflow-x-auto bg-grey-50 px-2"
        >
          {tabOrder.map((tabKey) => {
            const tab = tabsById[tabKey];
            if (!tab) return null;
            const selected = tabKey === activeTabId;
            const id = tabDomId(tabKey);
            return (
              <div
                key={tabKey}
                className={cn(
                  // Aucune bordure, quatre coins arrondis : l'onglet est une
                  // pastille posée sur la bande, pas une languette accrochée au
                  // contenu. L'actif se distingue par son blanc et une ombre
                  // douce ; les autres restent un cran plus foncés que la bande.
                  'flex h-8 min-w-[8rem] max-w-[13rem] items-center rounded-md transition-colors',
                  selected
                    ? 'bg-white shadow-sm'
                    : 'bg-grey-100 hover:bg-grey-200/70',
                )}
              >
                <button
                  id={id}
                  type="button"
                  role="tab"
                  aria-selected={selected}
                  aria-controls="document-preview-tabpanel"
                  tabIndex={selected ? 0 : -1}
                  title={tab.subtitle ?? tab.title}
                  onClick={() => activateTab(tabKey)}
                  onKeyDown={(event) => handleTabKeyDown(event, tabKey)}
                  className={cn(
                    'min-w-0 flex-1 truncate px-2 text-left text-xs font-medium',
                    selected ? 'text-charcoal' : 'text-grey-600 hover:text-grey-900',
                  )}
                >
                  {tab.title}
                </button>
                <button
                  type="button"
                  aria-label={`Fermer ${tab.title}`}
                  onClick={() => handleCloseTab(tabKey)}
                  className="mr-1 flex h-6 w-6 flex-shrink-0 items-center justify-center rounded text-grey-500 hover:bg-grey-200 hover:text-grey-900"
                >
                  <X className="h-3.5 w-3.5" aria-hidden="true" />
                </button>
              </div>
            );
          })}
        </div>

        {activeTab && (
          <div
            role="group"
            aria-label="Actions de l'aperçu actif"
            // Le filet du HAUT reste : il sépare la bande d'onglets de l'aperçu
            // ouvert. Seul celui du bas a disparu — la rangée d'actions et le
            // contenu sont une seule surface blanche.
            //
            // Hauteur INTRINSÈQUE (`py-2.5`) et non fixe : c'est cette rangée qui
            // porte tout l'espace entre le filet et le début du contenu, et les
            // corps d'onglet n'en rajoutent pas. Avec une hauteur figée, le
            // rembourrage du corps s'additionnait sous les boutons et les collait
            // au filet — ils paraissaient calés en haut plutôt que centrés.
            className="flex min-w-0 items-center gap-2 border-t border-grey-200 bg-white px-2 py-2.5"
          >
            {/* Pas de titre ici : l'onglet actif porte déjà le nom juste au-dessus.
                Deux ancres opposées à la place — ce qui change la VUE ouvre à
                gauche, ce qui agit SUR le contenu reste collé à droite. */}
            {activeTab.kind === 'document' && <DocumentPreviewTabViewToggle tab={activeTab} />}
            <div className="ml-auto flex flex-shrink-0 items-center gap-0.5">
              {activeTab.kind === 'document'
                ? <DocumentPreviewTabFileActions tab={activeTab} />
                : <DecisionPreviewTabActions tab={activeTab} />}
              <PreviewIconButton label="Fermer tous les aperçus" onClick={closePanel}>
                <X className="h-4 w-4" aria-hidden="true" />
              </PreviewIconButton>
            </div>
          </div>
        )}
      </div>

      {activeTab && (
        <div
          id="document-preview-tabpanel"
          role="tabpanel"
          aria-labelledby={activeTabDomId}
          className="relative min-h-0 w-full flex-1 overflow-hidden"
        >
          {activeTab.kind === 'document'
            ? <DocumentPreviewTabBody tab={activeTab} />
            : <DecisionPreviewTabBody tab={activeTab} />}
        </div>
      )}
    </aside>
  );
}
